export const name = "COSINE_DISTANCE";
export const summary = `Computes the cosine distance between two vectors.`;
export const reference = `
## COSINE_DISTANCE

Computes the cosine distance between two vectors.

\`\`\`
COSINE_DISTANCE(vector1, vector2)
\`\`\`

### Description

Computes the cosine distance between two vectors.


### Definitions

- \`vector1\`: A vector that's represented by an\`ARRAY<T>\` value or a sparse vector that is represented by an \`ARRAY<STRUCT<dimension,magnitude>>\` value.
- \`vector2\`: A vector that's represented by an\`ARRAY<T>\` value or a sparse vector that is represented by an \`ARRAY<STRUCT<dimension,magnitude>>\` value.


### Details

- \`ARRAY<T>\` can be used to represent a vector. Each zero-based index in this array represents a dimension. The value for each element in this array represents a magnitude.

- \`ARRAY<STRUCT<dimension,magnitude>>\` can be used to represent a sparse vector. With a sparse vector, you only need to include dimension-magnitude pairs for non-zero magnitudes. If a magnitude isn't present in the sparse vector, the magnitude is implicitly understood to be zero.

- Both non-sparse vectors in this function must share the same dimensions, and if they don't, an error is produced.

- A vector can't be a zero vector. A vector is a zero vector if it has no dimensions or all dimensions have a magnitude of \`0\`, such as \`[]\` or\`[0.0, 0.0]\`. If a zero vector is encountered, an error is produced.

- An error is produced if a magnitude in a vector is \`NULL\`.

- If a vector is \`NULL\`, \`NULL\` is returned.


### Return type

\`FLOAT64\`


### Examples

In the following example, non-sparsevectors are used to compute the cosine distance:



\`\`\`
SELECT COSINE_DISTANCE([1.0, 2.0], [3.0, 4.0]) AS results;

/*----------*
 | results |
 +----------+
 | 0.016130 |
 *----------*/
\`\`\`

In the following example, sparse vectors are used to compute the cosine distance:



\`\`\`
SELECT COSINE_DISTANCE(
 [(1, 1.0), (2, 2.0)],
 [(2, 4.0), (1, 3.0)]) AS results;

 /*----------*
  | results |
  +----------+
  | 0.016130 |
  *----------*/
\`\`\`

The ordering of numeric values in a vector doesn't impact the results produced by this function. For example these queries produce the same results even though the numeric values in each vector is in a different order:



\`\`\`
SELECT COSINE_DISTANCE([1.0, 2.0], [3.0, 4.0]) AS results;
\`\`\`



\`\`\`
SELECT COSINE_DISTANCE([2.0, 1.0], [4.0, 3.0]) AS results;
\`\`\`



\`\`\`
SELECT COSINE_DISTANCE([(1, 1.0), (2, 2.0)], [(1, 3.0), (2, 4.0)]) AS results;
\`\`\`



\`\`\`
/*----------*
  | results |
  +----------+
  | 0.016130 |
  *----------*/
\`\`\`

In the following example, the function can't compute cosine distance against the first vector, which is a zero vector:



\`\`\`
-- ERROR
SELECT COSINE_DISTANCE([0.0, 0.0], [3.0, 4.0]) AS results;
\`\`\`



\`\`\`
-- ERROR
SELECT COSINE_DISTANCE([(1, 0.0), (2, 0.0)], [(1, 3.0), (2, 4.0)]) AS results;
\`\`\`

Both non-sparse vectors must have the same dimensions. If not, an error is produced. In the following example, the first vector has two dimensions and the second vector has three:



\`\`\`
-- ERROR
SELECT COSINE_DISTANCE([9.0, 7.0], [8.0, 4.0, 5.0]) AS results;
\`\`\`

If you use sparse vectors and you repeat a dimension, an error is produced:



\`\`\`
-- ERROR
SELECT COSINE_DISTANCE(
  [(1, 9.0), (2, 7.0), (2, 8.0)], [(1, 8.0), (2, 4.0), (3, 5.0)]) AS results;
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/mathematical_functions
`;
